import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  useWorkflow,
  WellData,
  CasingString,
  PerforationStage,
  TreatmentStep,
  SurveyPoint,
  SurveyData,
  WellConfigData,
  UploadedSurvey,
} from './WorkflowContext.tsx';
import styles from './WellConfig.module.css';
import useTheme from '../../hooks/useTheme.ts';
import { 
  FiUpload, 
  FiPlus, 
  FiTrash2, 
  FiX,
  FiAlertCircle,
  FiFileText,
  FiNavigation,
  FiChevronDown,
  FiChevronUp,
  FiEye,
  FiTarget,
  FiDroplet,
  FiLayers,
  FiCheck,
  FiSettings,
  FiInfo
} from 'react-icons/fi';
import { HotTable } from '@handsontable/react';
import { registerAllModules } from 'handsontable/registry';
import 'handsontable/dist/handsontable.full.min.css';
import * as XLSX from 'xlsx';

// Register Handsontable modules
registerAllModules();

const backendUrl = process.env.REACT_APP_BACKEND_URL;

/** Module-level cache — prevents re-fetching pipe specs on component remounts */
let PIPE_SPECS_CACHE: PipeSpec[] | null = null;

// ============================================
// INTERFACES
// ============================================

// Treatment type options matching TreatmentModal
const TREATMENT_TYPE_OPTIONS = [
  'Acid',
  'Acid Spearhead',
  'Breakdown',
  'Displacement',
  'Diverter',
  'FET',
  'Flush',
  'Load',
  'Overflush',
  'Pad',
  'Pre-Flush',
  'Pre-Pad',
  'Proppant',
  'Pump-in',
  'Pumpdown',
  'Sand Ramp',
  'Shutdown',
  'Spacer',
  'Sweep'
];

// Description dictionary for proppant types (matching TreatmentModal)
const DESCRIPTION_DICTIONARY: { [key: string]: number } = {
  '30/70 White': 22.1,
  '30/80 FracBlack HT': 22.6,
  '40/70 Brady': 22.1,
  '40/70 White': 22.1,
  '40/70 CRC - E': 21.434,
  '40/70 PC': 21.43,
  '40/70 PPC': 21.43,
  '40/70 Optiprop-G2': 21.184,
  '40/70 ProLite': 22.101,
  '100 Mesh Native': 22.1,
  '40/70 Native': 22.1,
  '100 Super LC': 21.267,
  '200 Mesh': 22.101,
  '20/40 Brady': 22.1,
  '30/50 Brady': 22.1,
  '30/70 Brady': 22.1,
  '16/30 Brady': 22.1,
  '12/20 Brady': 22.1,
};

const getDescriptionOptions = (): string[] => Object.keys(DESCRIPTION_DICTIONARY).sort();
const getDescriptionValue = (description: string): number => DESCRIPTION_DICTIONARY[description] || 22.101;


// Standard casing grades
const CASING_GRADES = ['H-40', 'J-55', 'K-55', 'N-80', 'L-80', 'C-90', 'C-95', 'T-95', 'P-110', 'Q-125'];

interface PipeSpec {
  id: number;
  pipe_type: string;
  od_in: number;
  weight_lb_ft: number;
  upset_type: string | null;
  capacity_bbl_ft: number;
  capacity_gal_ft: number;
  id_in: number;
}

// ============================================
// COMPONENT
// ============================================

interface WellConfigProps {
  onNext?: () => void;
  isViewer?: boolean;
}

const WellConfig: React.FC<WellConfigProps> = ({ onNext, isViewer = false }) => {
  const { theme } = useTheme();
  const { state, setWells, addWell, updateWell, removeWell, updateWellConfig, setUploadedSurvey } = useWorkflow();
  const { wells, padData, wellConfigs, uploadedSurveys } = state;
  const isDark = theme === 'dark';

  const [showAddForm, setShowAddForm] = useState(false);
  const [configDragActive, setConfigDragActive] = useState<string | null>(null);
  const [surveyDragActive, setSurveyDragActive] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Record<string, { 
    wellbore: boolean; 
    perfs: boolean; 
    treatment: boolean; 
    survey: boolean 
  }>>({});
  const [showSurveyPreview, setShowSurveyPreview] = useState<string | null>(null);
  const [pipeSpecs, setPipeSpecs] = useState<PipeSpec[]>([]);
  const [showPerfModal, setShowPerfModal] = useState<string | null>(null);

  const [perfTableVersion, setPerfTableVersion] = useState<Record<string, number>>({});
  const [perfModalData, setPerfModalData] = useState({
    perfStart: 10000,
    stageSpacing: 150,
    perfLength: 120,
    clusters: 6,
    diameter: 0.42,
    holes: 6,
  });
  
  const configInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const surveyInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const hotTableRefs = useRef<Record<string, any>>({});
  // Refs for numeric well detail inputs (uncontrolled — no re-render while typing)
  const wellNumericRefs = useRef<Record<string, {
    stages?: HTMLInputElement | null;
    tvd?: HTMLInputElement | null;
    td?: HTMLInputElement | null;
    curveStart?: HTMLInputElement | null;
    curveEnd?: HTMLInputElement | null;
    maxPsi?: HTMLInputElement | null;
  }>>({});
  // Refs for casing depth inputs (uncontrolled — no re-render while typing)
  const casingInputRefs = useRef<Record<string, Record<string, {
    topDepth?: HTMLInputElement | null;
    bottomDepth?: HTMLInputElement | null;
  }>>>({});
  // Track wells whose survey was removed in UI — DB deletion deferred to deploy
  // (handled in WorkflowContext.setUploadedSurvey + deployWorkflow)

  // New well form state - all fields available on add
  const [newWell, setNewWell] = useState<Partial<WellData>>({
    name: '',
    api: '',
    afe: '',
    stages: 0,
    tvd: 0,
    td: 0,
    curveStart: 0,
    curveEnd: 0,
    maxPsi: 0,
    status: 'Planned',
  });

  // Derive section expand/collapse state inline — no effect, no extra render.
  // perfs intentionally starts collapsed: Handsontable with 100s of rows is
  // expensive to initialize and blocks the main thread. User can expand on demand.
  const getSections = (wellId: string, cfg: WellConfigData | undefined, srv: UploadedSurvey | undefined) => {
    if (expandedSections[wellId]) return expandedSections[wellId];
    return {
      wellbore: (cfg?.casingStrings?.length ?? 0) > 0,
      perfs: false,
      treatment: (cfg?.treatment?.length ?? 0) > 0,
      survey: !!srv,
    };
  };

  // Fetch pipe specs once per session (module-level cache)
  useEffect(() => {
    const fetchPipeSpecs = async () => {
      if (PIPE_SPECS_CACHE) {
        setPipeSpecs(PIPE_SPECS_CACHE);
        return;
      }
      try {
        const token = localStorage.getItem('token');
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;

        const response = await fetch(`${backendUrl}/api/reference/pipe-specs`, { headers });
        if (response.ok) {
          const data = await response.json();
          if (data && data.length > 0) {
            PIPE_SPECS_CACHE = data;
            setPipeSpecs(data);
          }
        }
      } catch (error) {
        console.error('Failed to fetch pipe specs:', error);
      }
    };
    fetchPipeSpecs();
  }, []);



  // Commit casing depth directly from DOM ref — no state update while typing
  const commitCasingDepth = (wellId: string, casingId: string, field: 'topDepth' | 'bottomDepth') => {
    const ref = casingInputRefs.current[wellId]?.[casingId]?.[field];
    const value = parseFloat(ref?.value ?? '') || 0;
    updateCasingString(wellId, casingId, field as keyof CasingString, value);
  };

  // Commit all numeric well fields directly from DOM refs — no state update while typing
  const commitWellNumerics = (wellId: string) => {
    const refs = wellNumericRefs.current[wellId];
    if (!refs) return;
    updateWell(wellId, {
      stages: Number(refs.stages?.value) || 0,
      tvd: Number(refs.tvd?.value) || 0,
      td: Number(refs.td?.value) || 0,
      curveStart: Number(refs.curveStart?.value) || 0,
      curveEnd: Number(refs.curveEnd?.value) || 0,
      maxPsi: Number(refs.maxPsi?.value) || 0,
    } as Partial<WellData>);
  };

  // ============================================
  // VOLUME CALCULATIONS
  // ============================================

  // Calculate effective lengths and volumes considering overlaps
  const calculateCasingVolumes = (casings: CasingString[]): CasingString[] => {
    if (casings.length === 0) return [];

    // Sort by ID (inner diameter) descending - largest to smallest
    const sorted = [...casings].sort((a, b) => b.pipeID - a.pipeID);
    
    return sorted.map((casing, index) => {
      // Find the next smaller casing that starts within this casing's range
      const smallerCasings = sorted.slice(index + 1);
      
      // Find where a smaller casing starts (if any overlaps)
      let effectiveBottom = casing.bottomDepth;
      
      for (const smaller of smallerCasings) {
        // If smaller casing starts within this casing's range
        if (smaller.topDepth >= casing.topDepth && smaller.topDepth < casing.bottomDepth) {
          // This casing's effective bottom is where the smaller one starts
          effectiveBottom = Math.min(effectiveBottom, smaller.topDepth);
          break;
        }
      }
      
      const effectiveLength = Math.max(0, effectiveBottom - casing.topDepth);
      const volume = effectiveLength * casing.capacityFactor;
      
      return {
        ...casing,
        effectiveLength,
        volume: Math.round(volume * 100) / 100,
      };
    });
  };

  // Get total wellbore volume
  const getTotalVolume = (casings: CasingString[]): number => {
    return casings.reduce((sum, c) => sum + c.volume, 0);
  };

  // ============================================
  // HANDLERS
  // ============================================

  const toggleSection = (wellId: string, section: 'wellbore' | 'perfs' | 'treatment' | 'survey') => {
    setExpandedSections(prev => {
      const current = prev[wellId] ?? {
        wellbore: (wellConfigs[wellId]?.casingStrings?.length ?? 0) > 0,
        perfs: false,
        treatment: (wellConfigs[wellId]?.treatment?.length ?? 0) > 0,
        survey: !!uploadedSurveys[wellId],
      };
      return {
        ...prev,
        [wellId]: { ...current, [section]: !current[section] },
      };
    });
  };

  // Casing/String handlers
  const addCasingString = (wellId: string, type: 'casing' | 'tubing' = 'casing') => {
    updateWellConfig(wellId, config => {
      const c = config || { file: null, uploadedAt: null, casingStrings: [], perforations: [], treatment: [] };
      const newCasing: CasingString = {
        id: `cs-${Date.now()}`,
        type,
        pipeOD: 0, pipeID: 0, weight: 0, grade: 'P-110',
        topDepth: 0, bottomDepth: 0, capacityFactor: 0, effectiveLength: 0, volume: 0,
      };
      return { ...c, casingStrings: [...c.casingStrings, newCasing] };
    });
  };

  const updateCasingString = (wellId: string, casingId: string, field: keyof CasingString, value: any) => {
    updateWellConfig(wellId, config => {
      if (!config) return config!;

      const updatedCasings = config.casingStrings.map(c => {
        if (c.id !== casingId) return c;
        
        let updated = { ...c, [field]: value };
        
        // If selecting a pipe OD, reset weight and dependent fields
        if (field === 'pipeOD') {
          const matchingSpecs = pipeSpecs.filter(p => p.od_in === value);
          if (matchingSpecs.length > 0 && value > 0) {
            // Don't auto-select weight, let user choose
            updated.weight = 0;
            updated.capacityFactor = 0;
            updated.pipeID = 0;
          } else {
            // Clear all if no OD selected
            updated.weight = 0;
            updated.capacityFactor = 0;
            updated.pipeID = 0;
          }
        }
        
        // If selecting weight, update capacity factor from spec
        if (field === 'weight' && value > 0) {
          const spec = pipeSpecs.find(p => p.od_in === c.pipeOD && p.weight_lb_ft === value);
          if (spec) {
            updated.capacityFactor = spec.capacity_bbl_ft;
            updated.pipeID = spec.id_in;
          }
        } else if (field === 'weight' && value === 0) {
          // Clear dependent fields if weight cleared
          updated.capacityFactor = 0;
          updated.pipeID = 0;
        }
        
        // If changing type, reset OD/weight options
        if (field === 'type') {
          updated.pipeOD = 0;
          updated.weight = 0;
          updated.capacityFactor = 0;
          updated.pipeID = 0;
        }
        
        return updated;
      });

      // Recalculate volumes
      const recalculated = calculateCasingVolumes(updatedCasings);
      return { ...config, casingStrings: recalculated };
    });
  };

  // Get unique pipe ODs from specs
  const getAvailableODs = (type: 'casing' | 'tubing'): number[] => {
    const ods = [...new Set(pipeSpecs.filter(p => p.pipe_type === type).map(p => p.od_in))];
    return ods.sort((a, b) => b - a);
  };

  // Get weights available for a specific OD
  const getWeightsForOD = (od: number): number[] => {
    const weights = pipeSpecs.filter(p => p.od_in === od).map(p => p.weight_lb_ft);
    return [...new Set(weights)].sort((a, b) => a - b);
  };

  // Treatment handlers - mirroring TreatmentModal functionality
  // Calculate derived fields for a treatment step
  const recalcTreatmentStep = (step: TreatmentStep): TreatmentStep => {
    const descValue = getDescriptionValue(step.description);
    const slurry = step.clean_step + (step.clean_step * step.conc / descValue);
    const sand = step.clean_step * 42 * step.conc;
    const time = step.rate > 0 ? (step.clean_step / step.rate) * 60 : 0;
    return { ...step, slurry_step: slurry, sand_step: sand, time_step: time };
  };

  // Renumber steps sequentially
  const renumberSteps = (steps: TreatmentStep[]): TreatmentStep[] =>
    steps.map((s, i) => ({ ...s, step: i + 1 }));

  const addTreatmentStep = (wellId: string, afterIndex?: number) => {
    updateWellConfig(wellId, config => {
      const c = config || { file: null, uploadedAt: null, casingStrings: [], perforations: [], treatment: [] };
      const newStep: TreatmentStep = {
        id: Date.now(),
        step: 0,
        type: '',
        description: '',
        rate: 0,
        conc: 0,
        clean_step: 0,
        slurry_step: 0,
        sand_step: 0,
        time_step: 0,
      };
      
      let updated: TreatmentStep[];
      if (afterIndex !== undefined && afterIndex >= 0) {
        updated = [...c.treatment];
        updated.splice(afterIndex + 1, 0, newStep);
      } else {
        updated = [...c.treatment, newStep];
      }
      return { ...c, treatment: renumberSteps(updated) };
    });
  };

  const copyTreatmentStep = (wellId: string, index: number) => {
    updateWellConfig(wellId, config => {
      if (!config) return config!;
      const source = config.treatment[index];
      if (!source) return config;
      const copied = { ...source, id: Date.now() };
      const updated = [...config.treatment];
      updated.splice(index + 1, 0, copied);
      return { ...config, treatment: renumberSteps(updated) };
    });
  };

  const moveTreatmentStep = (wellId: string, index: number, direction: 'up' | 'down') => {
    updateWellConfig(wellId, config => {
      if (!config) return config!;
      const arr = [...config.treatment];
      const targetIdx = direction === 'up' ? index - 1 : index + 1;
      if (targetIdx < 0 || targetIdx >= arr.length) return config;
      [arr[index], arr[targetIdx]] = [arr[targetIdx], arr[index]];
      return { ...config, treatment: renumberSteps(arr) };
    });
  };

  const updateTreatmentStep = (wellId: string, index: number, field: keyof TreatmentStep, value: string | number) => {
    updateWellConfig(wellId, config => {
      if (!config) return config!;
      const updatedTreatment = config.treatment.map((step, i) => {
        if (i !== index) return step;
        let updated = { ...step, [field]: value };
        
        // If type changed away from Proppant/Sand Ramp, clear description
        if (field === 'type') {
          const wasProppant = step.type === 'Proppant' || step.type === 'Sand Ramp';
          const isProppant = value === 'Proppant' || value === 'Sand Ramp';
          if (wasProppant && !isProppant) {
            updated.description = '';
          }
        }
        
        // Recalculate derived fields
        return recalcTreatmentStep(updated);
      });

      return { ...config, treatment: updatedTreatment };
    });
  };

  const removeTreatmentStep = (wellId: string, index: number) => {
    updateWellConfig(wellId, config => {
      if (!config) return config!;
      const filtered = config.treatment.filter((_, i) => i !== index);
      return { ...config, treatment: renumberSteps(filtered) };
    });
  };

  // Context menu state for treatment table
  const [treatmentContextMenu, setTreatmentContextMenu] = useState<{
    visible: boolean;
    x: number;
    y: number;
    wellId: string;
    stepIndex: number;
  }>({ visible: false, x: 0, y: 0, wellId: '', stepIndex: -1 });
  const treatmentContextMenuRef = useRef<HTMLDivElement>(null);

  // Close context menu on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (treatmentContextMenuRef.current && !treatmentContextMenuRef.current.contains(event.target as Node)) {
        setTreatmentContextMenu(prev => ({ ...prev, visible: false }));
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleTreatmentRightClick = (e: React.MouseEvent, wellId: string, stepIndex: number) => {
    e.preventDefault();
    setTreatmentContextMenu({ visible: true, x: e.clientX, y: e.clientY, wellId, stepIndex });
  };

  // Sand Ramp Concentration Modal state
  const [concModal, setConcModal] = useState<{
    visible: boolean;
    wellId: string;
    stepIndex: number;
    startConc: number;
    endConc: number;
  }>({ visible: false, wellId: '', stepIndex: -1, startConc: 0, endConc: 0 });

  const openConcModal = (wellId: string, stepIndex: number) => {
    const step = wellConfigs[wellId]?.treatment[stepIndex];
    if (!step) return;
    setConcModal({
      visible: true,
      wellId,
      stepIndex,
      startConc: step.start_conc ?? step.conc ?? 0,
      endConc: step.end_conc ?? step.conc ?? 0,
    });
  };

  const saveConcModal = () => {
    const { wellId, stepIndex, startConc, endConc } = concModal;
    const avgConc = (startConc + endConc) / 2;
    updateWellConfig(wellId, config => {
      if (!config) return config!;
      const updated = config.treatment.map((step, i) =>
        i !== stepIndex ? step : recalcTreatmentStep({ ...step, conc: avgConc, start_conc: startConc, end_conc: endConc })
      );
      return { ...config, treatment: updated };
    });
    setConcModal(prev => ({ ...prev, visible: false }));
  };

  // Autopopulate perforations
  const autopopulatePerforations = (wellId: string) => {
    const well = wells.find(w => w.id === wellId);
    if (!well) return;

    const existingPerfs = wellConfigs[wellId]?.perforations?.length || 0;
    if (existingPerfs > 0) {
      const proceed = window.confirm('Autopopulate will replace existing perforations. Continue?');
      if (!proceed) return;
    }

    const { perfStart, stageSpacing, perfLength, clusters, diameter, holes } = perfModalData;
    const td = well.td;
    
    const perforations: PerforationStage[] = [];
    let currentDepth = perfStart;
    let stage = 1;

    // Keep adding stages until we exceed TD
    while (currentDepth + perfLength <= td) {
      // Generate one row per cluster within this stage
      const clusterSpacing = clusters > 1 ? perfLength / clusters : perfLength;
      for (let ci = 0; ci < clusters; ci++) {
        const clusterTop = currentDepth + ci * clusterSpacing;
        const clusterBottom = clusterTop + clusterSpacing;
        perforations.push({
          stage,
          clusters: ci + 1,     // cluster NUMBER, not count
          topPerf: Math.round(clusterTop * 100) / 100,
          bottomPerf: Math.round(clusterBottom * 100) / 100,
          diameter,
          holes,
        });
      }
      
      currentDepth += stageSpacing;
      stage++;
      
      // Safety limit
      if (stage > 500) break;
    }

    updateWellConfig(wellId, config => ({
      ...(config || { file: null, uploadedAt: null, casingStrings: [], perforations: [], treatment: [] }),
      perforations,
    }));

    // Update well stages count
    const uniqueStages = new Set(perforations.map(p => p.stage)).size;
    updateWell(wellId, { stages: uniqueStages });

    setShowPerfModal(null);
    setPerfTableVersion(prev => ({
      ...prev,
      [wellId]: (prev[wellId] || 0) + 1,
    }));
  };

  // Handle paste from Excel for perforations
  // Expected column order: Stage, Cluster #, Top Perf, Bottom Perf, Diameter, Holes
  const handlePerfPaste = (e: React.ClipboardEvent, wellId: string) => {
    const pastedText = e.clipboardData.getData('text');
    if (!pastedText) return;

    // Parse tab-separated or comma-separated data from Excel
    const rows = pastedText.trim().split('\n').map(row => 
      row.split(/\t|,/).map(cell => cell.trim())
    );

    if (rows.length === 0) return;

    // Expected columns: Stage, Clusters, Top Perf, Bottom Perf, Diameter, Holes
    const perforations: PerforationStage[] = rows
      .filter(row => row.length >= 6 && !isNaN(parseInt(row[0]))) // Skip header rows
      .map(row => ({
        stage: parseInt(row[0]) || 0,
        clusters: parseInt(row[1]) || 0,
        topPerf: parseFloat(row[2]) || 0,
        bottomPerf: parseFloat(row[3]) || 0,
        diameter: parseFloat(row[4]) || 0,
        holes: parseInt(row[5]) || 0,
      }));

    if (perforations.length === 0) return;

    e.preventDefault();

    updateWellConfig(wellId, config => ({
      ...(config || { file: null, uploadedAt: null, casingStrings: [], perforations: [], treatment: [] }),
      perforations,
    }));

    // Update well stages count
    const uniqueStages = new Set(perforations.map(p => p.stage)).size;
    updateWell(wellId, { stages: uniqueStages });
  };

  // Update a single perforation field
  const updatePerforation = (wellId: string, stageNum: number, field: keyof PerforationStage, value: number) => {
    updateWellConfig(wellId, config => {
      if (!config) return config!;
      return { ...config, perforations: config.perforations.map(p => p.stage === stageNum ? { ...p, [field]: value } : p) };
    });
  };

  // Add a new perforation row
  const addPerforation = (wellId: string) => {
    updateWellConfig(wellId, config => {
      const c = config || { file: null, uploadedAt: null, casingStrings: [], perforations: [], treatment: [] };
      const lastPerf = c.perforations[c.perforations.length - 1];
      const newPerf: PerforationStage = {
        stage: lastPerf ? lastPerf.stage + 1 : 1,
        clusters: 1,
        topPerf: lastPerf ? lastPerf.bottomPerf + 30 : 10000,
        bottomPerf: lastPerf ? lastPerf.bottomPerf + 150 : 10120,
        diameter: lastPerf?.diameter || 0.42,
        holes: lastPerf?.holes || 6,
      };
      return { ...c, perforations: [...c.perforations, newPerf] };
    });
  };

  // Remove a perforation row
  const removePerforation = (wellId: string, stageNum: number) => {
    updateWellConfig(wellId, config => {
      if (!config) return config!;
      return { ...config, perforations: config.perforations.filter(p => p.stage !== stageNum) };
    });
  };


  const removeCasingString = (wellId: string, casingId: string) => {
    updateWellConfig(wellId, config => {
      if (!config) return config!;
      const filtered = config.casingStrings.filter(c => c.id !== casingId);
      return { ...config, casingStrings: calculateCasingVolumes(filtered) };
    });
  };

  // Config file handlers
  const handleConfigDrag = (e: React.DragEvent, wellId: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setConfigDragActive(wellId);
    } else if (e.type === 'dragleave') {
      setConfigDragActive(null);
    }
  };

  const handleConfigDrop = (e: React.DragEvent, wellId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setConfigDragActive(null);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleConfigFile(e.dataTransfer.files[0], wellId);
    }
  };

  const handleConfigFileInput = (e: React.ChangeEvent<HTMLInputElement>, wellId: string) => {
    if (e.target.files && e.target.files[0]) {
      handleConfigFile(e.target.files[0], wellId);
    }
  };

  const handleConfigFile = (file: File, wellId: string) => {
    if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
      // Store the file reference — actual data comes from the backend
      // or from the sub-section editors (casing table, perf grid, treatment grid)
      updateWellConfig(wellId, existing => ({
        file,
        uploadedAt: new Date(),
        casingStrings: existing?.casingStrings || [],
        perforations: existing?.perforations || [],
        treatment: existing?.treatment || [],
      }));

      // Auto-expand sections so the user can start entering data
      setExpandedSections(prev => ({
        ...prev,
        [wellId]: { wellbore: true, perfs: true, treatment: true, survey: false },
      }));
    } else {
      alert('Please upload an Excel file (.xlsx or .xls)');
    }
  };

  const removeConfig = (wellId: string) => {
    updateWellConfig(wellId, () => null as any);
  };

  // Survey file handlers
  const handleSurveyDrag = (e: React.DragEvent, wellId: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setSurveyDragActive(wellId);
    } else if (e.type === 'dragleave') {
      setSurveyDragActive(null);
    }
  };

  const handleSurveyDrop = (e: React.DragEvent, wellId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setSurveyDragActive(null);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleSurveyFile(e.dataTransfer.files[0], wellId);
    }
  };

  const handleSurveyFileInput = (e: React.ChangeEvent<HTMLInputElement>, wellId: string) => {
    if (e.target.files && e.target.files[0]) {
      handleSurveyFile(e.target.files[0], wellId);
    }
  };

  const handleSurveyFile = (file: File, wellId: string) => {
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      alert('Please upload an Excel file (.xlsx or .xls)');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const ws = workbook.Sheets[workbook.SheetNames[0]];

        // Helper to get cell value
        const cell = (ref: string): any => {
          const c = ws[ref];
          return c ? c.v : null;
        };

        // Parse header metadata (matches Gordon Technologies survey report format)
        // Row 3: Well (C3), Latitude (I3)
        // Row 4: County (C4), Longitude (I4), Mag Ref (N4)
        // Row 5: State (C5), Datum (I5)
        // Row 7: API (C7)
        // Row 2: Report Date (N2)
        // Row 11: Survey Calc (C11)
        const header: SurveyData['header'] = {
          well: String(cell('C3') || ''),
          api: String(cell('C7') || ''),
          reportDate: String(cell('N2') || ''),
          latitude: parseFloat(cell('I3')) || 0,
          longitude: parseFloat(cell('I4')) || 0,
          surveyCalc: String(cell('C11') || ''),
          magReference: String(cell('N4') || ''),
        };

        // Parse survey rows starting at row 14
        // A=Survey, B=MD, C=Inc, D=Azimuth, E=TVD, F=VS, G=NS, H=EW,
        // I=CourseLength, J=DLS, K=Temp, L=ClosureDist, M=ClosureAzi, N=Source
        const surveys: SurveyPoint[] = [];
        let row = 14;
        const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');

        while (row <= range.e.r + 1) {
          const mdVal = cell(`B${row}`);
          if (mdVal === null || mdVal === undefined) {
            row++;
            continue;
          }
          const md = parseFloat(mdVal);
          if (isNaN(md)) {
            row++;
            continue;
          }

          const surveyLabel = cell(`A${row}`);
          surveys.push({
            survey: surveyLabel === 'Tie In' || surveyLabel === null ? 'Tie In' : Number(surveyLabel) || surveyLabel,
            md,
            inc: parseFloat(cell(`C${row}`)) || 0,
            azimuth: parseFloat(cell(`D${row}`)) || 0,
            tvd: parseFloat(cell(`E${row}`)) || 0,
            vs: parseFloat(cell(`F${row}`)) || 0,
            ns: parseFloat(cell(`G${row}`)) || 0,
            ew: parseFloat(cell(`H${row}`)) || 0,
            dls: parseFloat(cell(`J${row}`)) || 0,
            source: cell(`N${row}`) ? String(cell(`N${row}`)) : null,
          });
          row++;
        }

        // Update well surface coordinates if available from survey header
        if (header.latitude && header.longitude) {
          updateWell(wellId, {
            surfaceLatitude: header.latitude,
            surfaceLongitude: header.longitude,
          });
        }

        setUploadedSurvey(wellId, {
          file,
          data: { header, surveys },
          uploadedAt: new Date(),
        });

        console.log(`Parsed ${surveys.length} survey points for well ${wellId}`);
      } catch (err) {
        console.error('Failed to parse survey file:', err);
        alert('Failed to parse survey file. Please check the format matches the expected survey report layout.');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const removeSurvey = (wellId: string) => {
    // setUploadedSurvey(null) automatically tracks DB wells for deletion on deploy
    setUploadedSurvey(wellId, null);
    setShowSurveyPreview(null);
  };

  // Well field update handlers
  const handleWellFieldChange = (wellId: string, field: keyof WellData, value: string | number) => {
    updateWell(wellId, { [field]: typeof value === 'string' && ['stages', 'tvd', 'td', 'curveStart', 'curveEnd'].includes(field) ? Number(value) || 0 : value });
  };

  // Add well handlers - creates well with all metrics at once
  const handleAddWell = () => {
    if (!newWell.name) return;

    const well: WellData = {
      id: `well-${Date.now()}`,
      name: newWell.name || '',
      api: newWell.api || '',
      afe: newWell.afe || '',
      stages: newWell.stages || 0,
      tvd: newWell.tvd || 0,
      td: newWell.td || 0,
      curveStart: newWell.curveStart || 0,
      curveEnd: newWell.curveEnd || 0,
      maxPsi: newWell.maxPsi || 0,
      status: 'Active', // Default status, not user-selectable on add
    };

    addWell(well);
    setNewWell({
      name: '',
      api: '',
      afe: '',
      stages: 0,
      tvd: 0,
      td: 0,
      curveStart: 0,
      curveEnd: 0,
      maxPsi: 0,
      status: 'Planned',
    });
    setShowAddForm(false);
  };

  // Utility functions
  const getTotalStages = () => wells.reduce((sum, w) => sum + w.stages, 0);
  const getConfiguredWells = () => Object.keys(wellConfigs).filter(id => {
    const c = wellConfigs[id];
    return c?.file || c?.casingStrings?.length > 0 || c?.perforations?.length > 0 || c?.treatment?.length > 0;
  }).length;
  const getSurveysUploaded = () => Object.keys(uploadedSurveys).length;

  const getStepTypeColor = (stepType: string) => {
    switch (stepType.trim()) {
      case 'Breakdown': return '#FF5F4D';
      case 'Acid':
      case 'Acid Spearhead': return '#F59E0B';
      case 'Pad':
      case 'Pre-Pad': return '#3E76FE';
      case 'Proppant': return '#8CC540';
      case 'Sand Ramp': return '#BD9EFF';
      case 'Flush':
      case 'Pre-Flush': return '#EC4899';
      case 'Spacer': return '#06B6D4';
      case 'Displacement': return '#F97316';
      case 'Diverter': return '#EF4444';
      case 'Sweep': return '#14B8A6';
      case 'Shutdown': return '#6B7280';
      default: return '#9CA3AF';
    }
  };

  // ============================================
  // WELLBORE SCHEMATIC COMPONENT (Bezier - Fixed)
  // ============================================

  const WellboreSchematic: React.FC<{ 
    casings: CasingString[]; 
    td: number; 
    tvd: number;
    curveStart: number;
    curveEnd: number;
  }> = ({ casings, td, tvd, curveStart, curveEnd }) => {
    if (casings.length === 0) return null;

    // SVG dimensions
    const svgWidth = 550;
    const svgHeight = 300;
    const padding = { top: 40, right: 150, bottom: 50, left: 70 };
    
    const drawWidth = svgWidth - padding.left - padding.right;
    const drawHeight = svgHeight - padding.top - padding.bottom;
    
    // Well geometry - use actual depths
    const actualCurveStart = curveStart || tvd * 0.9;
    const actualCurveEnd = curveEnd || tvd;
    const lateralLength = td - actualCurveEnd;
    
    // Allocate drawing space
    const verticalHeight = Math.min(drawHeight * 0.45, 90);
    const curveSize = Math.min(drawHeight * 0.35, 70);
    const lateralWidth = Math.max(drawWidth * 0.5, 150);
    
    // Starting point (wellhead)
    const wellheadX = padding.left + 40;
    const wellheadY = padding.top;
    
    // Key points
    const curveStartY = wellheadY + verticalHeight;
    const curveEndX = wellheadX + curveSize;
    const curveEndY = curveStartY + curveSize;
    const tdX = curveEndX + lateralWidth;
    const tdY = curveEndY;
    
    // Sort casings by OD descending (draw largest first)
    const sortedCasings = [...casings].sort((a, b) => b.pipeOD - a.pipeOD);
    const maxOD = Math.max(...casings.map(c => c.pipeOD));
    const minOD = Math.min(...casings.map(c => c.pipeOD));
    
    // Scale pipe widths to show visible differences
    // Larger OD = wider pipe on screen
    // Use a range from 8px (smallest) to 28px (largest) for better visibility
    const minPipeWidth = 10;
    const maxPipeWidth = 30;
    const getScaledWidth = (od: number) => {
      if (maxOD === minOD) return (minPipeWidth + maxPipeWidth) / 2;
      const ratio = (od - minOD) / (maxOD - minOD);
      return minPipeWidth + ratio * (maxPipeWidth - minPipeWidth);
    };

    // Distinct colors for each casing - more saturated and different hues
    const colors = [
      '#5B8BD4', // Blue
      '#7CC47C', // Green  
      '#D4A85B', // Orange/Gold
      '#D45B8B', // Pink
      '#8B5BD4', // Purple
      '#5BD4C4', // Teal
      '#D4D45B', // Yellow
      '#D45B5B', // Red
    ];

    // Helper to convert MD (measured depth) to screen Y position
    const mdToScreenY = (md: number): number => {
      if (md <= actualCurveStart) {
        // In vertical section
        return wellheadY + (md / actualCurveStart) * verticalHeight;
      }
      // Beyond curve start - clamp to curve start Y
      return curveStartY;
    };

    // Helper to convert MD to screen X position (for lateral)
    const mdToScreenX = (md: number): number => {
      if (md <= actualCurveEnd) {
        return curveEndX;
      }
      // In lateral section
      const lateralProgress = Math.min((md - actualCurveEnd) / lateralLength, 1);
      return curveEndX + lateralProgress * lateralWidth;
    };

    const getCasingPath = (pipeWidth: number, topMD: number, bottomMD: number): string => {
      const hw = pipeWidth / 2;
      
      // Arc center is at the OUTSIDE corner (top-right of the bend)
      const arcCenterX = wellheadX + curveSize;
      const arcCenterY = curveStartY;
      const radius = curveSize;
      const innerRadius = Math.max(radius - hw, 5);
      const outerRadius = radius + hw;
      const numSegments = 20;
      
      // Determine which sections this casing spans
      const startsInVertical = topMD < actualCurveStart;
      const startsInCurve = topMD >= actualCurveStart && topMD < actualCurveEnd;
      const startsInLateral = topMD >= actualCurveEnd;
      
      const endsInVertical = bottomMD <= actualCurveStart;
      const endsInCurve = bottomMD > actualCurveStart && bottomMD <= actualCurveEnd;
      const endsInLateral = bottomMD > actualCurveEnd;
      
      // Calculate start and end positions
      const lateralEndX = endsInLateral 
        ? curveEndX + Math.min((bottomMD - actualCurveEnd) / lateralLength, 1) * lateralWidth
        : curveEndX;
      
      const lateralStartX = startsInLateral
        ? curveEndX + Math.min((topMD - actualCurveEnd) / lateralLength, 1) * lateralWidth
        : curveEndX;
      
      let path = '';
      
      // CASE 1: Casing only in vertical section
      if (endsInVertical) {
        const startY = mdToScreenY(topMD);
        const endY = mdToScreenY(bottomMD);
        return `
          M ${wellheadX - hw} ${startY}
          L ${wellheadX - hw} ${endY}
          L ${wellheadX + hw} ${endY}
          L ${wellheadX + hw} ${startY}
          Z
        `;
      }
      
      // CASE 2: Casing only in lateral section  
      if (startsInLateral) {
        return `
          M ${lateralStartX} ${curveEndY - hw}
          L ${lateralEndX} ${curveEndY - hw}
          L ${lateralEndX} ${curveEndY + hw}
          L ${lateralStartX} ${curveEndY + hw}
          Z
        `;
      }
      
      // CASE 3: Casing starts in vertical and goes through curve
      if (startsInVertical) {
        const startY = mdToScreenY(topMD);
        
        // Start at top-left of casing
        path += `M ${wellheadX - hw} ${startY}`;
        
        // Down the left side of vertical to curve start
        path += ` L ${wellheadX - hw} ${curveStartY}`;
        
        // Connecting line if needed
        const innerCurveStartX = arcCenterX - innerRadius;
        if (innerCurveStartX > wellheadX - hw) {
          path += ` L ${innerCurveStartX} ${curveStartY}`;
        }
        
        // Draw inner edge of curve
        for (let i = 0; i <= numSegments; i++) {
          const angle = Math.PI - (i / numSegments) * (Math.PI/2);
          const x = arcCenterX + innerRadius * Math.cos(angle);
          const y = arcCenterY + innerRadius * Math.sin(angle);
          path += ` L ${x} ${y}`;
        }
        
        // Connect to lateral if needed
        const innerCurveEndY = arcCenterY + innerRadius;
        if (innerCurveEndY < curveEndY - hw) {
          path += ` L ${curveEndX} ${curveEndY - hw}`;
        }
        
        // Along top of lateral to end
        path += ` L ${lateralEndX} ${curveEndY - hw}`;
        
        // End cap
        path += ` L ${lateralEndX} ${curveEndY + hw}`;
        
        // Back along bottom of lateral
        const outerCurveEndY = arcCenterY + outerRadius;
        path += ` L ${curveEndX} ${curveEndY + hw}`;
        
        if (outerCurveEndY < curveEndY + hw) {
          path += ` L ${curveEndX} ${outerCurveEndY}`;
        }
        
        // Draw outer edge of curve (backwards)
        for (let i = numSegments; i >= 0; i--) {
          const angle = Math.PI - (i / numSegments) * (Math.PI/2);
          const x = arcCenterX + outerRadius * Math.cos(angle);
          const y = arcCenterY + outerRadius * Math.sin(angle);
          path += ` L ${x} ${y}`;
        }
        
        // Connect back to vertical
        const outerCurveStartX = arcCenterX - outerRadius;
        if (outerCurveStartX < wellheadX + hw) {
          path += ` L ${wellheadX + hw} ${curveStartY}`;
        }
        
        // Up the right side of vertical
        path += ` L ${wellheadX + hw} ${startY}`;
        
        path += ' Z';
        return path;
      }
      
      // CASE 4: Casing starts in curve (partial curve + lateral)
      if (startsInCurve) {
        const curveProgress = (topMD - actualCurveStart) / (actualCurveEnd - actualCurveStart);
        const startSegment = Math.floor(curveProgress * numSegments);
        
        // Start on inner edge of curve
        const startAngle = Math.PI - (startSegment / numSegments) * (Math.PI/2);
        const startX = arcCenterX + innerRadius * Math.cos(startAngle);
        const startY = arcCenterY + innerRadius * Math.sin(startAngle);
        
        path += `M ${startX} ${startY}`;
        
        // Continue inner curve from start point
        for (let i = startSegment; i <= numSegments; i++) {
          const angle = Math.PI - (i / numSegments) * (Math.PI/2);
          const x = arcCenterX + innerRadius * Math.cos(angle);
          const y = arcCenterY + innerRadius * Math.sin(angle);
          path += ` L ${x} ${y}`;
        }
        
        // Connect to lateral
        const innerCurveEndY = arcCenterY + innerRadius;
        if (innerCurveEndY < curveEndY - hw) {
          path += ` L ${curveEndX} ${curveEndY - hw}`;
        }
        
        // Along lateral
        path += ` L ${lateralEndX} ${curveEndY - hw}`;
        path += ` L ${lateralEndX} ${curveEndY + hw}`;
        path += ` L ${curveEndX} ${curveEndY + hw}`;
        
        // Outer curve back to start
        const outerCurveEndY = arcCenterY + outerRadius;
        if (outerCurveEndY < curveEndY + hw) {
          path += ` L ${curveEndX} ${outerCurveEndY}`;
        }
        
        for (let i = numSegments; i >= startSegment; i--) {
          const angle = Math.PI - (i / numSegments) * (Math.PI/2);
          const x = arcCenterX + outerRadius * Math.cos(angle);
          const y = arcCenterY + outerRadius * Math.sin(angle);
          path += ` L ${x} ${y}`;
        }
        
        path += ' Z';
        return path;
      }
      
      return '';
    };

    return (
      <div className={styles.schematicContainer}>
        <h5 className={styles.schematicTitle}>Wellbore Schematic</h5>
        <svg width={svgWidth} height={svgHeight} className={styles.schematicSvg}>
          {/* Background */}
          <rect x={0} y={0} width={svgWidth} height={svgHeight} fill={isDark ? '#1A1D1F' : '#F8FAFC'} rx={6} />
          
          {/* Ground surface line */}
          <line 
            x1={padding.left - 10} 
            y1={wellheadY} 
            x2={wellheadX + 60} 
            y2={wellheadY} 
            stroke={isDark ? '#4A5568' : '#94A3B8'} 
            strokeWidth={2}
          />
          <text 
            x={padding.left - 15} 
            y={wellheadY + 4} 
            fontSize={10} 
            fill={isDark ? '#9CA3AF' : '#64748B'}
            textAnchor="end"
          >
            Surface
          </text>

          {/* Draw casings from outside (largest) to inside (smallest) */}
          {sortedCasings.map((casing, index) => {
            const pipeWidth = getScaledWidth(casing.pipeOD);
            const color = colors[index % colors.length];
            
            if (casing.bottomDepth <= casing.topDepth) return null;

            return (
              <path
                key={casing.id}
                d={getCasingPath(pipeWidth, casing.topDepth, casing.bottomDepth)}
                fill={color}
                stroke={isDark ? '#1A1D1F' : '#FFFFFF'}
                strokeWidth={1}
              />
            );
          })}

          {/* Wellhead */}
          <rect 
            x={wellheadX - 18} 
            y={wellheadY - 14} 
            width={36} 
            height={16} 
            fill={isDark ? '#374151' : '#1E293B'}
            rx={2}
          />
          <text 
            x={wellheadX} 
            y={wellheadY - 3} 
            fontSize={8} 
            fill="#FFFFFF" 
            textAnchor="middle"
          >
            Wellhead
          </text>

          {/* Curve Start depth label */}
          <text 
            x={padding.left - 15} 
            y={curveStartY + 4} 
            fontSize={9} 
            fill={isDark ? '#9CA3AF' : '#64748B'}
            textAnchor="end"
          >
            {actualCurveStart.toLocaleString()}'
          </text>
          <line 
            x1={padding.left - 10} 
            y1={curveStartY} 
            x2={wellheadX - 25} 
            y2={curveStartY} 
            stroke={isDark ? '#4A5568' : '#94A3B8'} 
            strokeWidth={1}
            strokeDasharray="3,3"
          />

          {/* TVD marker */}
          {tvd > 0 && (
            <>
              <text 
                x={padding.left - 15} 
                y={curveEndY + 4} 
                fontSize={9} 
                fill="#8CC540"
                textAnchor="end"
                fontWeight={600}
              >
                TVD {tvd.toLocaleString()}'
              </text>
              <line 
                x1={padding.left - 10} 
                y1={curveEndY} 
                x2={curveEndX + 20} 
                y2={curveEndY} 
                stroke="#8CC540" 
                strokeWidth={1.5}
                strokeDasharray="4,2"
              />
            </>
          )}

          {/* KOP label */}
          <text 
            x={curveEndX - 5} 
            y={curveEndY + 20} 
            fontSize={8} 
            fill={isDark ? '#6B7280' : '#94A3B8'}
            textAnchor="start"
          >
            KOP {actualCurveEnd.toLocaleString()}'
          </text>

          {/* TD label at end of lateral */}
          <text 
            x={tdX} 
            y={tdY + 30} 
            fontSize={9} 
            fill={isDark ? '#E5E7EB' : '#1E293B'}
            textAnchor="middle"
            fontWeight={600}
          >
            TD {td.toLocaleString()}'
          </text>
          <line 
            x1={tdX} 
            y1={tdY + 12} 
            x2={tdX} 
            y2={tdY + 20} 
            stroke={isDark ? '#9CA3AF' : '#64748B'} 
            strokeWidth={1}
          />

          {/* Section labels */}
          <text 
            x={wellheadX + 30} 
            y={(wellheadY + curveStartY) / 2} 
            fontSize={9} 
            fill={isDark ? '#6B7280' : '#94A3B8'}
            transform={`rotate(-90, ${wellheadX + 30}, ${(wellheadY + curveStartY) / 2})`}
            textAnchor="middle"
          >
            Vertical
          </text>
          
          <text 
            x={(curveEndX + tdX) / 2} 
            y={curveEndY - 20} 
            fontSize={9} 
            fill={isDark ? '#6B7280' : '#94A3B8'}
            textAnchor="middle"
          >
            Lateral Section ({lateralLength.toLocaleString()} ft)
          </text>

          {/* Legend */}
          <g transform={`translate(${svgWidth - padding.right + 20}, ${padding.top})`}>
            <text fontSize={10} fontWeight={600} fill={isDark ? '#E5E7EB' : '#1E293B'}>Casing Strings</text>
            {sortedCasings.slice(0, 6).map((casing, i) => (
              <g key={i} transform={`translate(0, ${18 + i * 24})`}>
                <rect x={0} y={0} width={16} height={16} fill={colors[i % colors.length]} rx={3} stroke={isDark ? '#1A1D1F' : '#FFFFFF'} strokeWidth={1} />
                <text x={22} y={8} fontSize={9} fill={isDark ? '#E5E7EB' : '#1E293B'} dominantBaseline="middle" fontWeight={500}>
                  {casing.pipeOD}" {casing.grade || casing.type}
                </text>
                <text x={22} y={20} fontSize={8} fill={isDark ? '#6B7280' : '#94A3B8'}>
                  to {casing.bottomDepth.toLocaleString()}'
                </text>
              </g>
            ))}
            
            {/* Volume summary */}
            <g transform={`translate(0, ${18 + Math.min(sortedCasings.length, 6) * 24 + 10})`}>
              <line x1={0} y1={0} x2={110} y2={0} stroke={isDark ? '#374151' : '#CBD5E1'} strokeWidth={1} />
              <text y={18} fontSize={9} fill={isDark ? '#9CA3AF' : '#64748B'}>Total Volume:</text>
              <text y={34} fontSize={12} fontWeight={600} fill="#8CC540">{getTotalVolume(sortedCasings).toFixed(1)} bbl</text>
            </g>
          </g>
        </svg>
        
        <div className={styles.schematicInfo}>
          <div className={styles.schematicInfoItem}>
            <span className={styles.schematicInfoLabel}>Vertical</span>
            <span className={styles.schematicInfoValue}>{tvd.toLocaleString()} ft</span>
          </div>
          <div className={styles.schematicInfoItem}>
            <span className={styles.schematicInfoLabel}>Lateral</span>
            <span className={styles.schematicInfoValue}>{lateralLength.toLocaleString()} ft</span>
          </div>
          <div className={styles.schematicInfoItem}>
            <span className={styles.schematicInfoLabel}>Total MD</span>
            <span className={styles.schematicInfoValue}>{td.toLocaleString()} ft</span>
          </div>
        </div>
      </div>
    );
  };

  // ============================================
  // RENDER
  // ============================================

  // Loading screen — stays up until loadPadFromApi's single atomic setState clears isLoading,
  // at which point wellConfigs and uploadedSurveys are guaranteed to be populated.
  if (state.isLoading) {
    return (
      <div className={`${styles.container} ${isDark ? styles.dark : ''}`}>
        <style>{`@keyframes wc-spin { to { transform: rotate(360deg); } }`}</style>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '360px',
          gap: '16px',
        }}>
          <div style={{
            width: 36,
            height: 36,
            border: `3px solid ${isDark ? '#374151' : '#e5e7eb'}`,
            borderTopColor: '#3b82f6',
            borderRadius: '50%',
            animation: 'wc-spin 0.75s linear infinite',
          }} />
          <p style={{
            color: isDark ? '#9ca3af' : '#6b7280',
            fontSize: 14,
            margin: 0,
          }}>
            Loading well configuration...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.container} ${isDark ? styles.dark : ''}`}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h2 className={styles.title}>Configure Wells</h2>
          <p className={styles.subtitle}>
            Add wells and upload configuration files for each well
          </p>
        </div>
        <div className={styles.headerActions}>
          {!isViewer && (
            <button className={styles.btnPrimary} onClick={() => setShowAddForm(true)}>
              <FiPlus /> Add Well
            </button>
          )}
        </div>
      </div>

      {/* Summary Bar */}
      {wells.length > 0 && (
        <div className={styles.summary}>
          <div className={styles.summaryItem}>
            <span className={styles.summaryValue}>{wells.length}</span>
            <span className={styles.summaryLabel}>Wells</span>
          </div>
          <div className={styles.summaryDivider} />
          <div className={styles.summaryItem}>
            <span className={styles.summaryValue}>{getConfiguredWells()}</span>
            <span className={styles.summaryLabel}>Configured</span>
          </div>
          <div className={styles.summaryDivider} />
          <div className={styles.summaryItem}>
            <span className={styles.summaryValue}>{getTotalStages()}</span>
            <span className={styles.summaryLabel}>Total Stages</span>
          </div>
          <div className={styles.summaryDivider} />
          <div className={styles.summaryItem}>
            <span className={styles.summaryValue}>{getSurveysUploaded()}</span>
            <span className={styles.summaryLabel}>Surveys</span>
          </div>
        </div>
      )}

      {/* Add Well Form - Complete well information in one screen */}
      {showAddForm && !isViewer && (
        <div className={styles.addWellForm}>
          <div className={styles.formHeader}>
            <h3>Add New Well</h3>
            <button className={styles.closeBtn} onClick={() => setShowAddForm(false)}>
              <FiX />
            </button>
          </div>
          
          {/* Row 1: Well Name, API, and AFE */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <div className={styles.formGroup}>
              <label>Well Name *</label>
              <input
                type="text"
                value={newWell.name}
                onChange={(e) => setNewWell({ ...newWell, name: e.target.value })}
                placeholder="e.g., TEX-HARVEY #2081"
              />
            </div>
            <div className={styles.formGroup}>
              <label>API Number</label>
              <input
                type="text"
                value={newWell.api}
                onChange={(e) => setNewWell({ ...newWell, api: e.target.value })}
                placeholder="e.g., 4217338842"
              />
            </div>
            <div className={styles.formGroup}>
              <label>AFE Number</label>
              <input
                type="text"
                value={newWell.afe}
                onChange={(e) => setNewWell({ ...newWell, afe: e.target.value })}
                placeholder="e.g., ODR210331"
              />
            </div>
          </div>

          {/* Row 2: Well Metrics - All in one row */}
          <div className={styles.formGridMetrics}>
            <div className={styles.formGroup}>
              <label>Stages</label>
              <input
                type="number"
                value={newWell.stages || ''}
                onChange={(e) => setNewWell({ ...newWell, stages: parseInt(e.target.value) || 0 })}
                placeholder="0"
              />
            </div>
            <div className={styles.formGroup}>
              <label>TVD (ft)</label>
              <input
                type="number"
                value={newWell.tvd || ''}
                onChange={(e) => setNewWell({ ...newWell, tvd: parseInt(e.target.value) || 0 })}
                placeholder="0"
              />
            </div>
            <div className={styles.formGroup}>
              <label>TD (ft)</label>
              <input
                type="number"
                value={newWell.td || ''}
                onChange={(e) => setNewWell({ ...newWell, td: parseInt(e.target.value) || 0 })}
                placeholder="0"
              />
            </div>
            <div className={styles.formGroup}>
              <label>Curve Start (ft)</label>
              <input
                type="number"
                value={newWell.curveStart || ''}
                onChange={(e) => setNewWell({ ...newWell, curveStart: parseInt(e.target.value) || 0 })}
                placeholder="0"
              />
            </div>
            <div className={styles.formGroup}>
              <label>Curve End (ft)</label>
              <input
                type="number"
                value={newWell.curveEnd || ''}
                onChange={(e) => setNewWell({ ...newWell, curveEnd: parseInt(e.target.value) || 0 })}
                placeholder="0"
              />
            </div>
            <div className={styles.formGroup}>
              <label>Max PSI</label>
              <input
                type="number"
                value={newWell.maxPsi || ''}
                onChange={(e) => setNewWell({ ...newWell, maxPsi: parseInt(e.target.value) || 0 })}
                placeholder="0"
              />
            </div>
          </div>

          <div className={styles.formActions}>
            <button className={styles.btnSecondary} onClick={() => setShowAddForm(false)}>
              Cancel
            </button>
            <button className={styles.btnPrimary} onClick={handleAddWell} disabled={!newWell.name}>
              <FiPlus /> Add Well
            </button>
          </div>
        </div>
      )}

      {/* Wells List */}
      <div className={styles.wellsList}>
        <div className={styles.wellsHeader}>
          <span>Wells ({wells.length})</span>
        </div>

        {wells.length === 0 ? (
          <div className={styles.emptyState}>
            <FiAlertCircle className={styles.emptyIcon} />
            <span>No wells configured</span>
            <p>Click "Add Well" to create a new well</p>
          </div>
        ) : (
          wells.map((well) => {
            const config = wellConfigs[well.id];
            const survey = uploadedSurveys[well.id];
            const sections = getSections(well.id, config, survey);
            const totalVolume = config ? getTotalVolume(config.casingStrings) : 0;

            return (
              <div key={well.id} className={styles.wellCard}>
                {/* Well Info Header */}
                <div className={styles.wellInfo}>
                  <div className={styles.wellHeaderRow}>
                    <h4 className={styles.wellName}>{well.name}</h4>
                    {(config?.file || config?.casingStrings?.length > 0 || config?.perforations?.length > 0 || config?.treatment?.length > 0) && (
                      <span className={styles.configBadge}>
                        <FiCheck /> Configured
                      </span>
                    )}
                  </div>

                  {/* Editable Well Details Grid - All metrics in one row */}
                  <div className={styles.wellDetailsGrid}>
                    <div className={styles.wellDetailItem}>
                      <label>API</label>
                      <input
                        key={`${well.id}-api`}
                        type="text"
                        defaultValue={well.api ?? ''}
                        onBlur={(e) => updateWell(well.id, { api: e.target.value })}
                        disabled={isViewer}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur();
                        }}
                        placeholder="API Number"
                      />
                    </div>
                    <div className={styles.wellDetailItem}>
                      <label>AFE</label>
                      <input
                        key={`${well.id}-afe`}
                        type="text"
                        defaultValue={well.afe ?? ''}
                        onBlur={(e) => updateWell(well.id, { afe: e.target.value })}
                        disabled={isViewer}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur();
                        }}
                        placeholder="AFE Number"
                      />
                    </div>
                    <div className={styles.wellDetailItem}>
                      <label>Stages</label>
                      <input
                        key={`${well.id}-stages`}
                        ref={el => {
                          if (!wellNumericRefs.current[well.id]) wellNumericRefs.current[well.id] = {};
                          wellNumericRefs.current[well.id].stages = el;
                        }}
                        type="number"
                        defaultValue={well.stages ?? ''}
                        onBlur={() => commitWellNumerics(well.id)}
                        disabled={isViewer}
                        placeholder="0"
                      />
                    </div>
                    <div className={styles.wellDetailItem}>
                      <label>TVD (ft)</label>
                      <input
                        key={`${well.id}-tvd`}
                        ref={el => {
                          if (!wellNumericRefs.current[well.id]) wellNumericRefs.current[well.id] = {};
                          wellNumericRefs.current[well.id].tvd = el;
                        }}
                        type="number"
                        defaultValue={well.tvd ?? ''}
                        onBlur={() => commitWellNumerics(well.id)}
                        disabled={isViewer}
                        placeholder="0"
                      />
                    </div>
                    <div className={styles.wellDetailItem}>
                      <label>TD (ft)</label>
                      <input
                        key={`${well.id}-td`}
                        ref={el => {
                          if (!wellNumericRefs.current[well.id]) wellNumericRefs.current[well.id] = {};
                          wellNumericRefs.current[well.id].td = el;
                        }}
                        type="number"
                        defaultValue={well.td ?? ''}
                        onBlur={() => commitWellNumerics(well.id)}
                        disabled={isViewer}
                        placeholder="0"
                      />
                    </div>
                    <div className={styles.wellDetailItem}>
                      <label>Curve Start (ft)</label>
                      <input
                        key={`${well.id}-curveStart`}
                        ref={el => {
                          if (!wellNumericRefs.current[well.id]) wellNumericRefs.current[well.id] = {};
                          wellNumericRefs.current[well.id].curveStart = el;
                        }}
                        type="number"
                        defaultValue={well.curveStart ?? ''}
                        onBlur={() => commitWellNumerics(well.id)}
                        disabled={isViewer}
                        placeholder="0"
                      />
                    </div>
                    <div className={styles.wellDetailItem}>
                      <label>Curve End (ft)</label>
                      <input
                        key={`${well.id}-curveEnd`}
                        ref={el => {
                          if (!wellNumericRefs.current[well.id]) wellNumericRefs.current[well.id] = {};
                          wellNumericRefs.current[well.id].curveEnd = el;
                        }}
                        type="number"
                        defaultValue={well.curveEnd ?? ''}
                        onBlur={() => commitWellNumerics(well.id)}
                        disabled={isViewer}
                        placeholder="0"
                      />
                    </div>
                    <div className={styles.wellDetailItem}>
                      <label>Max PSI</label>
                      <input
                        key={`${well.id}-maxPsi`}
                        ref={el => {
                          if (!wellNumericRefs.current[well.id]) wellNumericRefs.current[well.id] = {};
                          wellNumericRefs.current[well.id].maxPsi = el;
                        }}
                        type="number"
                        defaultValue={well.maxPsi ?? ''}
                        onBlur={() => commitWellNumerics(well.id)}
                        disabled={isViewer}
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>

                {/* Config Upload Zone */}
                <div
                  className={`${styles.configUploadZone} ${configDragActive === well.id ? styles.configUploadActive : ''} ${config?.file ? styles.configUploaded : ''}`}
                  onDragEnter={(e) => !isViewer && handleConfigDrag(e, well.id)}
                  onDragLeave={(e) => !isViewer && handleConfigDrag(e, well.id)}
                  onDragOver={(e) => !isViewer && handleConfigDrag(e, well.id)}
                  onDrop={(e) => !isViewer && handleConfigDrop(e, well.id)}
                  onClick={() => !isViewer && !config?.file && configInputRefs.current[well.id]?.click()}
                >
                  <input
                    ref={(el) => configInputRefs.current[well.id] = el}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => handleConfigFileInput(e, well.id)}
                    className={styles.hiddenInput}
                  />
                  {config?.file ? (
                    <div className={styles.configFileInfo}>
                      <FiFileText className={styles.configFileIcon} />
                      <div className={styles.configFileDetails}>
                        <span className={styles.configFileName}>{config.file.name}</span>
                        <span className={styles.configFileDate}>
                          Uploaded {config.uploadedAt?.toLocaleString()}
                        </span>
                      </div>
                      {!isViewer && <button
                        className={styles.configRemoveBtn}
                        onClick={(e) => { e.stopPropagation(); removeConfig(well.id); }}
                      >
                        <FiX />
                      </button>}
                    </div>
                  ) : (
                    <>
                      <FiUpload className={styles.configUploadIcon} />
                      <span className={styles.configUploadText}>
                        Drop configuration_rswa.xlsx here or click to browse
                      </span>
                      <span className={styles.configUploadHint}>
                        Populates wellbore config, perforations, and treatment schedule
                      </span>
                    </>
                  )}
                </div>

                {/* Wellbore Configuration Section */}
                <div className={styles.section}>
                  <button
                    className={styles.sectionToggle}
                    onClick={() => toggleSection(well.id, 'wellbore')}
                  >
                    <FiLayers className={styles.sectionIcon} style={{ color: '#3E76FE' }} />
                    <span>Wellbore Configuration</span>
                    <span className={styles.sectionCount}>
                      {config?.casingStrings.length || 0} strings | {totalVolume.toFixed(1)} bbl
                    </span>
                    {sections.wellbore ? <FiChevronUp /> : <FiChevronDown />}
                  </button>

                  {sections.wellbore && (
                    <div className={styles.sectionContent}>
                      {/* Casing Table */}
                      <div className={styles.casingTableContainer}>
                        <div className={styles.casingTableHeader}>
                          <span>Casing / String Configuration</span>
                          <div className={styles.addCasingBtns}>
                            {!isViewer && <button 
                              className={styles.addCasingBtn}
                              onClick={() => addCasingString(well.id, 'casing')}
                            >
                              <FiPlus /> Add Casing
                            </button>}
                            {!isViewer && <button 
                              className={styles.addCasingBtn}
                              onClick={() => addCasingString(well.id, 'tubing')}
                            >
                              <FiPlus /> Add Tubing
                            </button>}
                          </div>
                        </div>

                        {config?.casingStrings && config.casingStrings.length > 0 ? (
                          <div className={styles.tableWrapper}>
                            <table className={styles.casingTable}>
                              <thead>
                                <tr>
                                  <th>Type</th>
                                  <th>Pipe OD</th>
                                  <th>Weight (lb/ft)</th>
                                  <th>Pipe ID</th>
                                  <th>Grade</th>
                                  <th>Top (ft)</th>
                                  <th>Bottom (ft)</th>
                                  <th>Cap Factor (bbl/ft)</th>
                                  <th>Eff. Length (ft)</th>
                                  <th>Volume (bbl)</th>
                                  <th></th>
                                </tr>
                              </thead>
                              <tbody>
                                {config.casingStrings.map((casing) => (
                                  <tr key={casing.id}>
                                    <td>
                                      <select
                                        value={casing.type}
                                        onChange={(e) => updateCasingString(well.id, casing.id, 'type', e.target.value)}
                                        className={styles.casingSelect}
                                        disabled={isViewer}
                                      >
                                        <option value="casing">Casing</option>
                                        <option value="tubing">Tubing</option>
                                      </select>
                                    </td>
                                    <td>
                                      <select
                                        value={casing.pipeOD || ''}
                                        onChange={(e) => updateCasingString(well.id, casing.id, 'pipeOD', parseFloat(e.target.value) || 0)}
                                        className={styles.casingSelect}
                                        disabled={isViewer}
                                      >
                                        <option value="">Select OD</option>
                                        {getAvailableODs(casing.type).map(od => (
                                          <option key={od} value={od}>{od}"</option>
                                        ))}
                                      </select>
                                    </td>
                                    <td>
                                      <select
                                        value={casing.weight || ''}
                                        onChange={(e) => updateCasingString(well.id, casing.id, 'weight', parseFloat(e.target.value) || 0)}
                                        className={styles.casingSelect}
                                        disabled={isViewer}
                                        disabled={!casing.pipeOD}
                                      >
                                        <option value="">Select Weight</option>
                                        {getWeightsForOD(casing.pipeOD).map(weight => (
                                          <option key={weight} value={weight}>{weight}#</option>
                                        ))}
                                      </select>
                                    </td>
                                    <td className={styles.calculatedCell}>
                                      {casing.pipeID ? casing.pipeID.toFixed(3) : '—'}
                                    </td>
                                    <td>
                                      <select
                                        value={casing.grade}
                                        onChange={(e) => updateCasingString(well.id, casing.id, 'grade', e.target.value)}
                                        className={styles.casingSelect}
                                        disabled={isViewer}
                                      >
                                        <option value="">Select Grade</option>
                                        {CASING_GRADES.map(grade => (
                                          <option key={grade} value={grade}>{grade}</option>
                                        ))}
                                      </select>
                                    </td>
                                    <td>
                                      <input
                                        key={`${casing.id}-top`}
                                        ref={el => {
                                          if (!casingInputRefs.current[well.id]) casingInputRefs.current[well.id] = {};
                                          if (!casingInputRefs.current[well.id][casing.id]) casingInputRefs.current[well.id][casing.id] = {};
                                          casingInputRefs.current[well.id][casing.id].topDepth = el;
                                        }}
                                        type="number"
                                        defaultValue={casing.topDepth || ''}
                                        onBlur={() => commitCasingDepth(well.id, casing.id, 'topDepth')}
                                        className={styles.casingInput}
                                        disabled={isViewer}
                                        placeholder="0"
                                      />
                                    </td>
                                    <td>
                                      <input
                                        key={`${casing.id}-bottom`}
                                        ref={el => {
                                          if (!casingInputRefs.current[well.id]) casingInputRefs.current[well.id] = {};
                                          if (!casingInputRefs.current[well.id][casing.id]) casingInputRefs.current[well.id][casing.id] = {};
                                          casingInputRefs.current[well.id][casing.id].bottomDepth = el;
                                        }}
                                        type="number"
                                        defaultValue={casing.bottomDepth || ''}
                                        onBlur={() => commitCasingDepth(well.id, casing.id, 'bottomDepth')}
                                        className={styles.casingInput}
                                        disabled={isViewer}
                                        placeholder="0"
                                      />
                                    </td>
                                    <td className={styles.calculatedCell}>
                                      {casing.capacityFactor ? casing.capacityFactor.toFixed(4) : '—'}
                                    </td>
                                    <td className={styles.calculatedCell}>
                                      {casing.effectiveLength.toLocaleString()}
                                    </td>
                                    <td className={styles.volumeCell}>
                                      {casing.volume.toFixed(2)}
                                    </td>
                                    <td>
                                      {!isViewer && <button
                                        className={styles.removeCasingBtn}
                                        onClick={() => removeCasingString(well.id, casing.id)}
                                      >
                                        <FiTrash2 />
                                      </button>}
                                    </td>
                                  </tr>
                                ))}
                                <tr className={styles.totalRow}>
                                  <td colSpan={9} className={styles.totalLabel}>Total Wellbore Volume</td>
                                  <td className={styles.totalVolume}>{totalVolume.toFixed(2)} bbl</td>
                                  <td></td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div className={styles.emptySection}>
                            Upload a configuration file or add casing/tubing manually
                          </div>
                        )}
                      </div>

                      {/* Wellbore Schematic - Below Table */}
                      {config?.casingStrings && config.casingStrings.length > 0 && (
                        <WellboreSchematic 
                          casings={config.casingStrings} 
                          td={well.td} 
                          tvd={well.tvd}
                          curveStart={well.curveStart}
                          curveEnd={well.curveEnd}
                        />
                      )}
                    </div>
                  )}
                </div>

                {/* Perforation Schedule Section */}
                <div className={styles.section}>
                  <button
                    className={styles.sectionToggle}
                    onClick={() => toggleSection(well.id, 'perfs')}
                  >
                    <FiTarget className={styles.sectionIcon} style={{ color: '#F59E0B' }} />
                    <span>Perforation Schedule</span>
                    <span className={styles.sectionCount}>
                      {config?.perforations ? new Set(config.perforations.map(p => p.stage)).size : 0} stages / {config?.perforations.length || 0} clusters
                    </span>
                    {sections.perfs ? <FiChevronUp /> : <FiChevronDown />}
                  </button>

                  {sections.perfs && (
                    <div className={styles.sectionContent}>
                      <div className={styles.sectionHeader}>
                        <span>Perforation Stages</span>
                        <div className={styles.sectionHeaderActions}>
                          {/* <button 
                            className={styles.autopopulateBtn}
                            onClick={() => setShowPerfModal(well.id)}
                          >
                            <FiSettings /> Autopopulate
                          </button> */}
                        </div>
                      </div>
                      <div className={styles.perfGridHint}>
                        <FiInfo size={14} />
                        <span>Right-click to add/remove rows. Paste from Excel supported.</span>
                      </div>

                      {/* Autopopulate Modal */}
                      {showPerfModal === well.id && (
                        <div className={styles.modalOverlay} onClick={() => setShowPerfModal(null)}>
                          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                            <div className={styles.modalHeader}>
                              <h4>Autopopulate Perforations</h4>
                              <button className={styles.modalCloseBtn} onClick={() => setShowPerfModal(null)}>
                                <FiX />
                              </button>
                            </div>
                            <div className={styles.modalBody}>
                              <p className={styles.modalHint}>
                                Generate perforation stages automatically. Stages will be created until reaching TD ({well.td?.toLocaleString() || 0} ft).
                              </p>
                              <div className={styles.modalGrid}>
                                <div className={styles.modalField}>
                                  <label>Perf Start (ft)</label>
                                  <input
                                    type="number"
                                    value={perfModalData.perfStart}
                                    onChange={(e) => setPerfModalData(prev => ({ ...prev, perfStart: parseInt(e.target.value) || 0 }))}
                                  />
                                </div>
                                <div className={styles.modalField}>
                                  <label>Stage Spacing (ft)</label>
                                  <input
                                    type="number"
                                    value={perfModalData.stageSpacing}
                                    onChange={(e) => setPerfModalData(prev => ({ ...prev, stageSpacing: parseInt(e.target.value) || 0 }))}
                                  />
                                </div>
                                <div className={styles.modalField}>
                                  <label>Perf Length (ft)</label>
                                  <input
                                    type="number"
                                    value={perfModalData.perfLength}
                                    onChange={(e) => setPerfModalData(prev => ({ ...prev, perfLength: parseInt(e.target.value) || 0 }))}
                                  />
                                </div>
                                <div className={styles.modalField}>
                                  <label>Clusters</label>
                                  <input
                                    type="number"
                                    value={perfModalData.clusters}
                                    onChange={(e) => setPerfModalData(prev => ({ ...prev, clusters: parseInt(e.target.value) || 0 }))}
                                  />
                                </div>
                                <div className={styles.modalField}>
                                  <label>Diameter (in)</label>
                                  <input
                                    type="number"
                                    step="0.01"
                                    value={perfModalData.diameter}
                                    onChange={(e) => setPerfModalData(prev => ({ ...prev, diameter: parseFloat(e.target.value) || 0 }))}
                                  />
                                </div>
                                <div className={styles.modalField}>
                                  <label>Holes/Cluster</label>
                                  <input
                                    type="number"
                                    value={perfModalData.holes}
                                    onChange={(e) => setPerfModalData(prev => ({ ...prev, holes: parseInt(e.target.value) || 0 }))}
                                  />
                                </div>
                              </div>
                              <div className={styles.modalPreview}>
                                <span className={styles.modalPreviewLabel}>Preview:</span>
                                <span className={styles.modalPreviewValue}>
                                  {well.td && well.td > perfModalData.perfStart ? (
                                    <>~{Math.floor((well.td - perfModalData.perfStart) / perfModalData.stageSpacing)} stages, {perfModalData.clusters} clusters each, {perfModalData.clusters * perfModalData.holes} total holes/stage</>
                                  ) : (
                                    <span style={{color: '#EF4444'}}>TD must be greater than Perf Start</span>
                                  )}
                                </span>
                              </div>
                            </div>
                            <div className={styles.modalFooter}>
                              <button className={styles.btnSecondary} onClick={() => setShowPerfModal(null)}>
                                Cancel
                              </button>
                              <button 
                                className={styles.btnPrimary} 
                                onClick={() => autopopulatePerforations(well.id)}
                                disabled={!perfModalData.perfStart || !perfModalData.stageSpacing || !well.td || well.td <= perfModalData.perfStart}
                              >
                                Generate {well.td && well.td > perfModalData.perfStart ? Math.floor((well.td - perfModalData.perfStart) / perfModalData.stageSpacing) : 0} Stages
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Perforation Grid - using Handsontable for Excel-like experience */}
                      <div className={styles.perfGridWrapper}>
                        <HotTable
                          ref={(el: any) => { if (el) hotTableRefs.current[well.id] = el; }}
                          key={`perf-table-${well.id}-${perfTableVersion[well.id] || 0}`}
                          readOnly={isViewer}
                          data={config?.perforations?.map(p => [p.stage, p.clusters, p.topPerf, p.bottomPerf, p.diameter, p.holes]) || []}
                          colHeaders={['Stage', 'Cluster #', 'Top Perf (ft)', 'Bottom Perf (ft)', 'Diameter (in)', 'Holes']}
                          columns={[
                            { 
                              type: 'numeric', 
                              className: 'htCenter',
                              validator: (value: any, callback: (valid: boolean) => void) => {
                                callback(value === null || value === '' || (Number.isInteger(Number(value)) && Number(value) >= 0));
                              }
                            },
                            { 
                              type: 'numeric', 
                              className: 'htCenter',
                              validator: (value: any, callback: (valid: boolean) => void) => {
                                callback(value === null || value === '' || (Number.isInteger(Number(value)) && Number(value) >= 0));
                              }
                            },
                            { 
                              type: 'numeric', 
                              numericFormat: { pattern: '0.00' }, 
                              className: 'htCenter' 
                            },
                            { 
                              type: 'numeric', 
                              numericFormat: { pattern: '0.00' }, 
                              className: 'htCenter' 
                            },
                            { 
                              type: 'numeric', 
                              numericFormat: { pattern: '0.000' }, 
                              className: 'htCenter' 
                            },
                            { 
                              type: 'numeric', 
                              className: 'htCenter',
                              validator: (value: any, callback: (valid: boolean) => void) => {
                                callback(value === null || value === '' || (Number.isInteger(Number(value)) && Number(value) >= 0));
                              }
                            },
                          ]}
                          rowHeaders={true}
                          height="auto"
                          minRows={1}
                          minSpareRows={1}
                          contextMenu={{
                            items: {
                              'row_above': { name: 'Insert row above' },
                              'row_below': { name: 'Insert row below' },
                              'remove_row': { name: 'Remove row' },
                              'separator': '---------',
                              'copy': { name: 'Copy' },
                              'cut': { name: 'Cut' },
                            }
                          }}
                          copyPaste={true}
                          manualColumnResize={true}
                          stretchH="all"
                          autoWrapRow={true}
                          autoWrapCol={true}
                          licenseKey="non-commercial-and-evaluation"
                          className={isDark ? 'htDark' : ''}
                          afterChange={(changes, source) => {
                            if (source === 'loadData' || !changes) return;
                            
                            // Access the Handsontable instance via React ref
                            const hotInstance = hotTableRefs.current[well.id]?.hotInstance;
                            if (!hotInstance) {
                              console.warn('HotTable ref not found for well:', well.id);
                              return;
                            }
                            
                            const tableData = hotInstance.getData();
                            const perforations: PerforationStage[] = tableData
                              .filter((row: any[]) => row[0] !== null && row[0] !== '' && row[0] !== undefined)
                              .map((row: any[]) => ({
                                stage: Math.round(Number(row[0])) || 0,
                                clusters: Math.round(Number(row[1])) || 0,
                                topPerf: Math.round(Number(row[2]) * 100) / 100 || 0,
                                bottomPerf: Math.round(Number(row[3]) * 100) / 100 || 0,
                                diameter: Math.round(Number(row[4]) * 1000) / 1000 || 0,
                                holes: Math.round(Number(row[5])) || 0,
                              }));
                            
                            updateWellConfig(well.id, config => ({
                              ...(config || { file: null, uploadedAt: null, casingStrings: [], perforations: [], treatment: [] }),
                              perforations,
                            }));
                            const uniqueStages = new Set(perforations.map(p => p.stage)).size;
                            updateWell(well.id, { stages: uniqueStages });
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Treatment Schedule Section */}
                <div className={styles.section}>
                  <button
                    className={styles.sectionToggle}
                    onClick={() => toggleSection(well.id, 'treatment')}
                  >
                    <FiDroplet className={styles.sectionIcon} style={{ color: '#EC4899' }} />
                    <span>Treatment Schedule</span>
                    <span className={styles.sectionCount}>
                      {config?.treatment.length || 0} steps
                    </span>
                    {sections.treatment ? <FiChevronUp /> : <FiChevronDown />}
                  </button>

                  {sections.treatment && (
                    <div className={styles.sectionContent}>
                      <div className={styles.sectionHeader}>
                        <span>Pump Schedule</span>
                      </div>
                      <div className={styles.perfGridWrapper}>
                        <div className={styles.tableWrapper}>
                          <table className={styles.dataTable}>
                            <thead>
                              {/* Header row 1 (matches your nested headers layout) */}
                              <tr>
                                <th colSpan={3}>Step</th>
                                <th colSpan={2}>Target</th>
                                <th>Clean</th>
                                <th>Slurry</th>
                                <th>Sand</th>
                                <th>Time</th>
                              </tr>

                              {/* Header row 2 (matches your column labels) */}
                              <tr>
                                <th>Step</th>
                                <th>Type</th>
                                <th>Description</th>
                                <th>Rate</th>
                                <th>Conc</th>
                                <th>Step</th>
                                <th>Step</th>
                                <th>Step</th>
                                <th>Step (min)</th>
                              </tr>
                            </thead>

                            <tbody>
                              {(config?.treatment || []).map((s, idx) => {
                                const isProppant = s.type === 'Proppant';
                                const isSandRamp = s.type === 'Sand Ramp';
                                const showDescDropdown = isProppant || isSandRamp;

                                return (
                                  <tr key={s.id} onContextMenu={(e) => { if (!isViewer) handleTreatmentRightClick(e, well.id, idx); }} style={{ cursor: 'context-menu' }}>
                                    {/* Step (readOnly, bold/center like HT) */}
                                    <td style={{ textAlign: 'center', fontWeight: 700 }}>
                                      {s.step}
                                    </td>

                                    {/* Type (dropdown) */}
                                    <td>
                                      <select
                                        className={styles.casingSelect}
                                        value={s.type || ''}
                                        onChange={(e) => updateTreatmentStep(well.id, idx, 'type', e.target.value)}
                                        disabled={isViewer}
                                      >
                                        <option value="">Select</option>
                                        {TREATMENT_TYPE_OPTIONS.map(opt => (
                                          <option key={opt} value={opt}>{opt}</option>
                                        ))}
                                      </select>
                                    </td>

                                    {/* Description (dropdown for Proppant/Sand Ramp; readOnly otherwise) */}
                                    <td>
                                      {showDescDropdown ? (
                                        <select
                                          className={styles.casingSelect}
                                          value={s.description || ''}
                                          onChange={(e) => updateTreatmentStep(well.id, idx, 'description', e.target.value)}
                                          disabled={isViewer}
                                        >
                                          <option value="">Select</option>
                                          {getDescriptionOptions().map(opt => (
                                            <option key={opt} value={opt}>{opt}</option>
                                          ))}
                                        </select>
                                      ) : (
                                        <input
                                          className={styles.casingInput}
                                          value={s.description || ''}
                                          readOnly
                                          placeholder=""
                                        />
                                      )}
                                    </td>

                                    {/* Rate */}
                                    <td>
                                      <input
                                        key={`${well.id}-${idx}-rate`}
                                        type="number"
                                        className={styles.casingInput}
                                        defaultValue={s.rate ?? 0}
                                        onBlur={(e) => updateTreatmentStep(well.id, idx, 'rate', Number(e.target.value) || 0)}
                                        disabled={isViewer}
                                      />
                                    </td>

                                    {/* Conc:
                                        - Proppant: editable input
                                        - Sand Ramp: readonly cell that opens modal (like your HT click behavior)
                                        - Others: readonly
                                    */}
                                    <td>
                                      {isProppant ? (
                                        <input
                                          key={`${well.id}-${idx}-conc`}
                                          type="number"
                                          step="0.01"
                                          className={styles.casingInput}
                                          defaultValue={s.conc ?? 0}
                                          onBlur={(e) => updateTreatmentStep(well.id, idx, 'conc', Number(e.target.value) || 0)}
                                          disabled={isViewer}
                                        />
                                      ) : isSandRamp ? (
                                        <button
                                          type="button"
                                          className={styles.btnSecondary}
                                          onClick={() => !isViewer && openConcModal(well.id, idx)}
                                          disabled={isViewer}
                                          title="Edit Sand Ramp concentrations"
                                          style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6 }}
                                        >
                                          <FiSettings />
                                          <span>
                                            {Number(s.conc || 0).toFixed(2)}
                                            <span style={{ fontSize: 10, color: '#9CA3AF', marginLeft: 6 }}>
                                              ({Number(s.start_conc ?? 0).toFixed(2)}/{Number(s.end_conc ?? 0).toFixed(2)})
                                            </span>
                                          </span>
                                        </button>
                                      ) : (
                                        <input
                                          className={styles.casingInput}
                                          value={Number(s.conc ?? 0).toFixed(2)}
                                          readOnly
                                          style={{ color: '#9CA3AF' }}
                                        />
                                      )}
                                    </td>

                                    {/* Clean Step */}
                                    <td>
                                      <input
                                        key={`${well.id}-${idx}-clean`}
                                        type="number"
                                        className={styles.casingInput}
                                        defaultValue={s.clean_step ?? 0}
                                        onBlur={(e) => updateTreatmentStep(well.id, idx, 'clean_step', Number(e.target.value) || 0)}
                                        disabled={isViewer}
                                      />
                                    </td>

                                    {/* Slurry Step (calculated) */}
                                    <td style={{ textAlign: 'center', color: isDark ? '#9CA3AF' : '#6B7280', fontStyle: 'italic' }}>
                                      {Number(s.slurry_step || 0).toFixed(2)}
                                    </td>

                                    {/* Sand Step (calculated) */}
                                    <td style={{ textAlign: 'center', color: isDark ? '#9CA3AF' : '#6B7280', fontStyle: 'italic' }}>
                                      {Number(s.sand_step || 0).toFixed(2)}
                                    </td>

                                    {/* Time Step (min) - your HT displayed (time_step/60) */}
                                    <td style={{ textAlign: 'center', color: isDark ? '#9CA3AF' : '#6B7280', fontStyle: 'italic' }}>
                                      {Number((s.time_step || 0) / 60).toFixed(2)}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>

                        {/* Optional: keep the same “Add Step” affordance near the grid like HT users expect */}
                        <div style={{ marginTop: 10, display: 'flex', gap: 10 }}>
                          {!isViewer && (
                            <button className={styles.addRowBtn} onClick={() => addTreatmentStep(well.id)}>
                              <FiPlus /> Add Step
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Directional Survey Section */}
                <div className={styles.section}>
                  <button
                    className={styles.sectionToggle}
                    onClick={() => toggleSection(well.id, 'survey')}
                  >
                    <FiNavigation className={styles.sectionIcon} style={{ color: '#8B5CF6' }} />
                    <span>Directional Survey</span>
                    {survey ? (
                      <span className={styles.sectionUploaded}>✓ Uploaded</span>
                    ) : (
                      <span className={styles.sectionMissing}>Not uploaded</span>
                    )}
                    {sections.survey ? <FiChevronUp /> : <FiChevronDown />}
                  </button>

                  {sections.survey && (
                    <div className={styles.sectionContent}>
                      {survey ? (
                        <>
                          <div className={styles.surveyFileRow}>
                            <FiFileText className={styles.surveyFileIcon} />
                            <div className={styles.surveyFileDetails}>
                              <span className={styles.surveyFileName}>{survey.file.name}</span>
                              <span className={styles.surveyFileDate}>
                                Uploaded {survey.uploadedAt.toLocaleString()}
                              </span>
                            </div>
                            <div className={styles.surveyFileActions}>
                              <button
                                className={styles.surveyPreviewBtn}
                                onClick={() => setShowSurveyPreview(showSurveyPreview === well.id ? null : well.id)}
                              >
                                <FiEye /> {showSurveyPreview === well.id ? 'Hide' : 'Preview'}
                              </button>
                              {!isViewer && (
                                <button
                                  className={styles.surveyRemoveBtn}
                                  onClick={() => removeSurvey(well.id)}
                                >
                                  <FiTrash2 />
                                </button>
                              )}
                            </div>
                          </div>

                          {showSurveyPreview === well.id && (
                            <div className={styles.surveyPreview}>
                              <div className={styles.surveyHeaderGrid}>
                                <div className={styles.surveyHeaderItem}>
                                  <span className={styles.surveyHeaderLabel}>Well</span>
                                  <span className={styles.surveyHeaderValue}>{survey.data.header.well}</span>
                                </div>
                                <div className={styles.surveyHeaderItem}>
                                  <span className={styles.surveyHeaderLabel}>API</span>
                                  <span className={styles.surveyHeaderValue}>{survey.data.header.api}</span>
                                </div>
                                <div className={styles.surveyHeaderItem}>
                                  <span className={styles.surveyHeaderLabel}>Report Date</span>
                                  <span className={styles.surveyHeaderValue}>{survey.data.header.reportDate}</span>
                                </div>
                                <div className={styles.surveyHeaderItem}>
                                  <span className={styles.surveyHeaderLabel}>Calc Method</span>
                                  <span className={styles.surveyHeaderValue}>{survey.data.header.surveyCalc}</span>
                                </div>
                                <div className={styles.surveyHeaderItem}>
                                  <span className={styles.surveyHeaderLabel}>Surface Lat/Lng</span>
                                  <span className={styles.surveyHeaderValue}>
                                    {survey.data.header.latitude.toFixed(7)}, {survey.data.header.longitude.toFixed(7)}
                                  </span>
                                </div>
                                <div className={styles.surveyHeaderItem}>
                                  <span className={styles.surveyHeaderLabel}>Survey Points</span>
                                  <span className={styles.surveyHeaderValue}>{survey.data.surveys.length}</span>
                                </div>
                              </div>
                              <div className={styles.tableWrapper}>
                                <table className={styles.dataTable}>
                                  <thead>
                                    <tr>
                                      <th>Survey</th>
                                      <th>MD (ft)</th>
                                      <th>Inc (°)</th>
                                      <th>Azimuth (°)</th>
                                      <th>TVD (ft)</th>
                                      <th>VS (ft)</th>
                                      <th>NS (ft)</th>
                                      <th>EW (ft)</th>
                                      <th>DLS</th>
                                      <th>Source</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {survey.data.surveys.map((point, idx) => (
                                      <tr key={idx}>
                                        <td>{point.survey}</td>
                                        <td>{Math.round(point.md)}</td>
                                        <td>{(+point.inc).toFixed(2)}</td>
                                        <td>{(+point.azimuth).toFixed(2)}</td>
                                        <td>{(+point.tvd).toFixed(2)}</td>
                                        <td>{(+point.vs).toFixed(2)}</td>
                                        <td>{(+point.ns).toFixed(3)}</td>
                                        <td>{(+point.ew).toFixed(3)}</td>
                                        <td>{(+point.dls).toFixed(2)}</td>
                                        <td>{point.source || '-'}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        <div
                          className={`${styles.surveyUploadZone} ${surveyDragActive === well.id ? styles.surveyUploadActive : ''}`}
                          onDragEnter={(e) => !isViewer && handleSurveyDrag(e, well.id)}
                          onDragLeave={(e) => !isViewer && handleSurveyDrag(e, well.id)}
                          onDragOver={(e) => !isViewer && handleSurveyDrag(e, well.id)}
                          onDrop={(e) => !isViewer && handleSurveyDrop(e, well.id)}
                          onClick={() => !isViewer && surveyInputRefs.current[well.id]?.click()}
                        >
                          <input
                            ref={(el) => surveyInputRefs.current[well.id] = el}
                            type="file"
                            accept=".xlsx,.xls"
                            onChange={(e) => handleSurveyFileInput(e, well.id)}
                            className={styles.hiddenInput}
                          />
                          <FiNavigation className={styles.surveyUploadIcon} />
                          <span className={styles.surveyUploadText}>
                            Drop directional survey file here or click to browse
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Well Actions */}
                <div className={styles.wellActions}>
                  <button
                    className={`${styles.actionBtn} ${styles.deleteBtn}`}
                    onClick={() => {
                      removeWell(well.id);
                      removeConfig(well.id);
                      removeSurvey(well.id);
                    }}
                    title="Remove well"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Validation Hints */}
      {wells.length === 0 && (
        <div className={styles.validationHint}>
          <FiAlertCircle />
          <span>Add at least one well to continue to scheduling</span>
        </div>
      )}

      {wells.length > 0 && getConfiguredWells() < wells.length && (
        <div className={styles.configReminder}>
          <FiUpload />
          <span>
            {wells.length - getConfiguredWells()} well(s) need configuration files uploaded
          </span>
        </div>
      )}
      {/* Sand Ramp Concentration Modal */}
      {concModal.visible && (
        <div className={styles.modalOverlay} onClick={() => setConcModal(prev => ({ ...prev, visible: false }))}>
          <div className={`${styles.modal} ${isDark ? styles.dark : ''}`} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h4>Sand Ramp Concentrations</h4>
              <button 
                className={styles.modalCloseBtn}
                onClick={() => setConcModal(prev => ({ ...prev, visible: false }))}
              >
                <FiX />
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.modalGrid}>
                <div className={styles.modalField}>
                  <label>Start Target Concentration</label>
                  <input
                    type="number"
                    step="0.01"
                    value={concModal.startConc}
                    onChange={(e) => setConcModal(prev => ({ ...prev, startConc: parseFloat(e.target.value) || 0 }))}
                    placeholder="0.00"
                  />
                </div>
                <div className={styles.modalField}>
                  <label>End Target Concentration</label>
                  <input
                    type="number"
                    step="0.01"
                    value={concModal.endConc}
                    onChange={(e) => setConcModal(prev => ({ ...prev, endConc: parseFloat(e.target.value) || 0 }))}
                    placeholder="0.00"
                  />
                </div>
              </div>
              {concModal.startConc > 0 && concModal.endConc > 0 && (
                <div className={styles.modalPreview}>
                  <span className={styles.modalPreviewLabel}>Average:</span>
                  <span className={styles.modalPreviewValue}>
                    {((concModal.startConc + concModal.endConc) / 2).toFixed(4)}
                  </span>
                </div>
              )}
            </div>
            <div className={styles.formActions}>
              <button
                className={styles.btnSecondary}
                onClick={() => setConcModal(prev => ({ ...prev, visible: false }))}
              >
                Cancel
              </button>
              <button
                className={styles.btnPrimary}
                onClick={saveConcModal}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Treatment Right-Click Context Menu */}
      {treatmentContextMenu.visible && (
        <div
          ref={treatmentContextMenuRef}
          style={{
            position: 'fixed',
            top: treatmentContextMenu.y,
            left: treatmentContextMenu.x,
            backgroundColor: isDark ? '#1E2124' : '#FFFFFF',
            border: `1px solid ${isDark ? '#303335' : '#E0E0E0'}`,
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 9999,
            padding: '4px 0',
            minWidth: '140px',
          }}
        >
          {[
            {
              label: 'Add Below',
              icon: '➕',
              action: () => {
                addTreatmentStep(treatmentContextMenu.wellId, treatmentContextMenu.stepIndex);
                setTreatmentContextMenu(prev => ({ ...prev, visible: false }));
              },
            },
            {
              label: 'Copy',
              icon: '📋',
              action: () => {
                copyTreatmentStep(treatmentContextMenu.wellId, treatmentContextMenu.stepIndex);
                setTreatmentContextMenu(prev => ({ ...prev, visible: false }));
              },
            },
            {
              label: 'Delete',
              icon: '🗑️',
              action: () => {
                removeTreatmentStep(treatmentContextMenu.wellId, treatmentContextMenu.stepIndex);
                setTreatmentContextMenu(prev => ({ ...prev, visible: false }));
              },
              danger: true,
            },
            { divider: true },
            {
              label: 'Move Up',
              icon: '⬆️',
              disabled: treatmentContextMenu.stepIndex === 0,
              action: () => {
                moveTreatmentStep(treatmentContextMenu.wellId, treatmentContextMenu.stepIndex, 'up');
                setTreatmentContextMenu(prev => ({ ...prev, visible: false }));
              },
            },
            {
              label: 'Move Down',
              icon: '⬇️',
              disabled: (() => {
                const config = wellConfigs[treatmentContextMenu.wellId];
                return !config || treatmentContextMenu.stepIndex >= config.treatment.length - 1;
              })(),
              action: () => {
                moveTreatmentStep(treatmentContextMenu.wellId, treatmentContextMenu.stepIndex, 'down');
                setTreatmentContextMenu(prev => ({ ...prev, visible: false }));
              },
            },
          ].map((item: any, i) =>
            item.divider ? (
              <div
                key={`div-${i}`}
                style={{
                  height: '1px',
                  background: isDark ? '#303335' : '#E0E0E0',
                  margin: '4px 0',
                }}
              />
            ) : (
              <button
                key={item.label}
                disabled={item.disabled}
                onClick={item.action}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  width: '100%',
                  padding: '8px 14px',
                  border: 'none',
                  background: 'transparent',
                  color: item.danger
                    ? '#DC3545'
                    : item.disabled
                    ? (isDark ? '#4B5563' : '#D1D5DB')
                    : (isDark ? '#E5E7EB' : '#1A1D1F'),
                  fontSize: '13px',
                  cursor: item.disabled ? 'not-allowed' : 'pointer',
                  textAlign: 'left',
                }}
                onMouseEnter={(e) => {
                  if (!item.disabled) {
                    (e.target as HTMLElement).style.background = isDark ? '#2A2D30' : '#F3F4F6';
                  }
                }}
                onMouseLeave={(e) => {
                  (e.target as HTMLElement).style.background = 'transparent';
                }}
              >
                <span style={{ fontSize: '14px' }}>{item.icon}</span>
                {item.label}
              </button>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default WellConfig;